import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Mail, Paperclip, Users, CalendarClock, RefreshCw } from "lucide-react";

const BASIS_LABEL = { daily: "yesterday", weekly: "the last 7 days", biweekly: "the last 14 days", monthly: "the previous month", quarterly: "the previous quarter", range: "your chosen dates" };

// iter376 — "what tomorrow's email will look like": the exact HTML the scheduler would send,
// rendered in a sandboxed iframe (no scripts), reflecting UNSAVED frequency / window / pending
// edits so a manager never has to wait a day to see a change.
export function ReportPreview({ tenantId, open, onClose, params }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true); setError(""); setData(null);
    try {
      const r = await api.post(`/tenants/${tenantId}/report/preview`, params || {});
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't build the preview — please try again.");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open, JSON.stringify(params)]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="report-preview-dialog" className="max-w-5xl w-[calc(100vw-2rem)] p-0 gap-0 overflow-hidden max-h-[calc(100dvh-2rem)] flex flex-col">
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-sand-200 text-left space-y-1.5">
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <Mail className="h-4 w-4 text-redline-600" /> Email preview
            {data?.basis && <span className="text-xs font-semibold text-ink/50 bg-sand-100 rounded-full px-2 py-0.5">{data.basis === "range" ? "Custom range" : `${data.frequency} · covers ${BASIS_LABEL[data.basis] || data.basis}`}</span>}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="text-sm text-ink/70">
              {data ? (
                <div className="space-y-1">
                  <div data-testid="report-preview-subject" className="text-ink font-medium truncate">Subject: {data.subject}</div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink/60">
                    {data.next_send_label && <span data-testid="report-preview-next" className="inline-flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Next send {data.next_send_label}</span>}
                    <span data-testid="report-preview-recipients" className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {data.recipients} recipient{data.recipients === 1 ? "" : "s"}</span>
                    <span className="inline-flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" /> {data.attach_pdf ? "PDF copy attached" : "No PDF attached"}</span>
                    <button type="button" data-testid="report-preview-refresh" onClick={load} className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-ink"><RefreshCw className="h-3 w-3" /> Refresh</button>
                  </div>
                </div>
              ) : <span>This is exactly what recipients get — built live from today's data{params?.frequency ? ", using the settings on this page (saved or not)" : ""}.</span>}
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="bg-[#f3f0ea] flex-1 min-h-0 overflow-hidden" style={{ height: "min(72vh, 900px)" }}>
          {loading && (
            <div data-testid="report-preview-loading" className="h-full flex items-center justify-center gap-2 text-ink/60 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Building the report from live data…</div>
          )}
          {!loading && error && <div data-testid="report-preview-error" className="h-full flex items-center justify-center text-sm text-redline-600 px-6 text-center">{error}</div>}
          {!loading && data && (
            <iframe data-testid="report-preview-frame" title="Report email preview" sandbox="" srcDoc={data.html}
              className="w-full h-full border-0 bg-[#f3f0ea]" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
